import { useLocation, useNavigate } from "react-router-dom";
import { useAssistantPanel } from "../contexts/AssistantPanelContext";
import { useRegisterCommands } from "../contexts/CommandRegistryContext";
import { useOptionalTheme } from "../providers/ThemeProvider";
import { buildSidebarMainItems } from "../components/Sidebar.config";
import { HIDDEN, READY } from "../services/commands/actionHelpers";
import type { CommandAction, CommandAvailability } from "../services/commands/commands.types";

/** Letter after "G" for each sidebar destination: "G" then the letter goes there. */
const GO_TO_KEYS: Readonly<Record<string, string>> = {
  home: "h", stories: "s", discover: "d", explore: "e", "data-connectors": "c", "knowledge-graph": "k",
  "data-studio": "t", agents: "a", automation: "u", activity: "y",
};
/** Destinations with their own "Go to" command elsewhere (home here, stories in the provider). */
const OWN_GO_TO = new Set(["home", "stories"]);

export const goToShortcut = (letter: string) => ({ chords: [{ key: "g" }, { key: letter }], sequence: true });

const HOME_PATH = /^\/home(\/|$)/;
const SETTINGS_PATH = /^\/settings(\/|$)/;

const hiddenOn = (pathname: string, pattern: RegExp): CommandAvailability => (pattern.test(pathname) ? HIDDEN : READY);

/**
 * App-wide commands that run an existing route or the theme toggle: go to home, open settings, toggle theme, Ask.
 * Keys: Mod+, (settings), Mod+Shift+L (theme), Mod+/ (Ask: the MagicBI panel, on every screen). None of them is a browser or OS chord.
 * Mounted once, near the app shell (inside the router and the theme provider). Go to stories lives in the provider.
 */
export const useAppCommands = (): void => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const theme = useOptionalTheme();
  const assistant = useAssistantPanel();

  // "Go to …" for every sidebar destination that has a route (not the ones marked Soon).
  const destinations: CommandAction[] = buildSidebarMainItems()
    .filter((item) => !item.soon && !OWN_GO_TO.has(item.id))
    .map((item) => {
      const name = item.label.toLowerCase();
      const letter = GO_TO_KEYS[item.id];
      return {
        id: `nav.${item.id}`, label: `Go to ${name}`, group: "navigation", keywords: [item.id.replace(/-/g, " ")],
        patterns: [`go to ${name}`, `open ${name}`],
        ...(letter ? { shortcut: goToShortcut(letter) } : {}),
        isAvailable: () => (item.isSelected(pathname) ? HIDDEN : READY),
        run: () => {
          navigate(item.to);
          return { status: "done", message: `Went to ${name}.` };
        },
      } as CommandAction;
    });

  const actions: CommandAction[] = [
    ...destinations,
    {
      id: "nav.home", label: "Go to home", group: "navigation", keywords: ["start", "dashboard", "main"],
      patterns: ["go to home", "go home", "open home"],
      shortcut: goToShortcut("h"),
      isAvailable: () => hiddenOn(pathname, HOME_PATH),
      run: () => {
        navigate("/home");
        return { status: "done", message: "Went to home." };
      },
    },
    {
      id: "app.ask", label: "Ask MagicBI", group: "assistant", keywords: ["chat", "ai", "question", "prompt"],
      patterns: ["ask magicbi", "open ask"],
      shortcut: { chords: [{ key: "/", mod: true }], allowInFields: true },
      isAvailable: () => READY,
      // One handler on every screen: the screen's own panel when it has one, else the generic MagicBI panel.
      run: () => {
        assistant.toggle();
        return { status: "done", message: assistant.isOpen ? "Closed MagicBI." : "Opened MagicBI." };
      },
    },
    {
      id: "nav.settings", label: "Open settings", group: "navigation", keywords: ["preferences", "account", "configure"],
      patterns: ["open settings", "go to settings"],
      shortcut: { chords: [{ key: ",", mod: true }] },
      isAvailable: () => hiddenOn(pathname, SETTINGS_PATH),
      run: () => {
        navigate("/settings");
        return { status: "done", message: "Opened settings." };
      },
    },
    {
      id: "theme.toggle", label: "Toggle theme", group: "navigation", keywords: ["dark", "light", "mode", "appearance"],
      patterns: ["toggle theme", "toggle dark mode", "switch theme"],
      shortcut: { chords: [{ key: "l", mod: true, shift: true }] },
      isAvailable: () => (theme ? READY : HIDDEN),
      run: () => {
        if (!theme) return { status: "failed", message: "Couldn't run Toggle theme." };
        const next = theme.resolvedMode === "dark" ? "light" : "dark";
        theme.toggleMode();
        return { status: "done", message: `Switched to ${next} mode.` };
      },
    },
  ];
  useRegisterCommands(actions);
};

/** "New story": opens the existing create-story flow (its AI or blank choice is made inside the flow). */
export const useCreateStoryCommand = (openFlow: () => void, openBlankFlow?: () => void): void => {
  useRegisterCommands([
    {
      id: "story.create", label: "New story", group: "navigation", keywords: ["create", "blank", "ai", "start"],
      patterns: ["new story", "create story", "create new story", "start new story"],
      isAvailable: () => READY,
      run: () => {
        openFlow();
        return { status: "done", message: "Started a new story." };
      },
    },
    {
      id: "story.createBlank", label: "New blank story", group: "navigation", keywords: ["create", "blank", "empty", "canvas"],
      patterns: ["new blank story", "create blank story"],
      isAvailable: () => (openBlankFlow ? READY : HIDDEN),
      run: () => {
        openBlankFlow?.();
        return { status: "done", message: "Started a blank story." };
      },
    },
  ]);
};
